import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import Button from '../Button/Button';
import {
  HazardSurveyResult,
  KnownHazardWithProtection,
} from './HazardSurveyResult';
import {
  ADD_JOB_ATTACHMENT,
  GET_HAZARD_SURVEY_JOB,
  SUBMIT_SURVEY_RESULTS,
} from './queries';
import { GET_WATER_HAZARDS } from '@/modes/admin/pages/waterHazards/WaterHazards.query';
import { HazardSurveySubmitPayload } from '@/services/hazardSurvey';
import { useUploadTempFile } from '@/hooks/useUploadTempFile';
import useSessionInfo from '../../hooks/useSessionInfo';

type Props = {
  jobId: string;
  onClose: () => void;
};

export default function HazardSurveyResultsModal({
  jobId,
  onClose,
}: Props): JSX.Element {
  const { session } = useSessionInfo();

  const { data: jobData, refetch } = useQuery(GET_HAZARD_SURVEY_JOB, {
    variables: { where: { id: jobId } },
  });

  const { data: hazardsData } = useQuery(GET_WATER_HAZARDS);

  const [submitSurveyResults] = useMutation(SUBMIT_SURVEY_RESULTS);
  const [addJobAttachment] = useMutation(ADD_JOB_ATTACHMENT);
  const { uploadTempFile } = useUploadTempFile();

  const job = jobData?.jobs?.[0];
  const survey = job?.waterHazardSurvey;

  const isSubmitted =
    survey?.status === 'IN_REVIEW' || survey?.status === 'ACCEPTED';

  const surveyTemplate = {
    instructions: survey?.instructions || '',
    otherHazardsQuestion: !!survey?.otherHazardsQuestion,
    protectedQuestion: true,
    hazardIds: (survey?.items ?? []).map((i: any) => i.waterHazardId),
  };

  const surveyHazards = (hazardsData?.waterHazards ?? []).filter((h: any) =>
    surveyTemplate.hazardIds.includes(h.id)
  );

  const knownHazards: KnownHazardWithProtection[] = (
    survey?.waterService?.waterServiceHazards ?? []
  ).map((h: any) => ({
    id: h.waterHazardId,
    name: h.waterHazard?.name ?? h.waterHazardId,
    hazardLevel: h.waterHazard?.hazardLevel ?? 'LOW',
    isProtected: h.isProtected ?? null,
    backflowAssemblyId: h.backflowAssemblyId ?? null,
  }));

  const formDataValues: HazardSurveySubmitPayload | undefined = survey
    ? {
        submitterName: survey.submitterName ?? undefined,
        submitterPhone: survey.submitterPhone ?? undefined,
        submitterEmail: survey.submitterEmail ?? undefined,
        comments: survey.comments ?? undefined,
        otherHazards: survey.otherHazardsAnswer ?? undefined,
        hazards: Object.fromEntries(
          (survey.items ?? []).map((i: any) => [i.waterHazardId, i.answer])
        ),
        protectedByHazard: Object.fromEntries(
          (survey.items ?? [])
            .filter((i: any) => i.isProtected != null)
            .map((i: any) => [i.waterHazardId, i.isProtected])
        ),
        backflowAssemblyByHazard: Object.fromEntries(
          (survey.items ?? []).map((i: any) => [
            i.waterHazardId,
            i.backflowAssemblyId,
          ])
        ),
      }
    : undefined;

  const [submitting, setSubmitting] = React.useState(false);
  const submitTriggerRef = React.useRef<((e: React.MouseEvent) => void) | null>(
    null
  );

  const handleRequestSubmit = async (values: HazardSurveySubmitPayload) => {
    setSubmitting(true);
    try {
      const items = (survey?.items ?? []).map((i: any) => ({
        waterHazardId: i.waterHazardId,
        answer: (values.hazards?.[i.waterHazardId] ?? 'UNKNOWN') as
          | 'YES'
          | 'NO'
          | 'UNKNOWN',
        isProtected: values.protectedByHazard?.[i.waterHazardId] as
          | 'YES'
          | 'NO'
          | 'UNKNOWN'
          | undefined,
        backflowAssemblyId:
          values.backflowAssemblyByHazard?.[i.waterHazardId] ?? undefined,
      }));
      await submitSurveyResults({
        variables: {
          jobId,
          input: {
            submitterName: values.submitterName,
            submitterPhone: values.submitterPhone,
            submitterEmail: values.submitterEmail,
            otherHazardsAnswer: values.otherHazards,
            comments: values.comments,
            items,
          },
        },
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadPhoto = async (file: File) => {
    const { key, contentType } = await uploadTempFile(file);
    await addJobAttachment({
      variables: {
        jobId,
        tempKey: key,
        contentType,
        source: 'WEB_UPLOAD',
      },
    });
    refetch();
  };

  const wsProfile = session?.companyProfile?.waterSupplier?.profile;
  const branding = wsProfile
    ? {
        name: wsProfile.name ?? null,
        logoUrl: wsProfile.image?.url ?? null,
      }
    : null;

  return (
    <Modal
      title={isSubmitted ? 'Survey Results' : 'Enter Survey Results'}
      onClose={onClose}
      sx={{ maxWidth: 900, boxShadow: 'none' }}
    >
      <ModalBody p={0}>
        <HazardSurveyResult
          template={surveyTemplate}
          hazards={surveyHazards}
          mode={isSubmitted ? 'results' : 'fill'}
          waterService={survey?.waterService as any}
          knownHazards={knownHazards}
          initialValues={formDataValues}
          onRequestSubmit={isSubmitted ? undefined : handleRequestSubmit}
          onUploadPhoto={isSubmitted ? undefined : handleUploadPhoto}
          attachments={job?.attachments ?? []}
          branding={branding}
          hideName
          hideHeader
          noShadow
          submitTriggerRef={isSubmitted ? undefined : submitTriggerRef}
        />
      </ModalBody>
      {!isSubmitted && (
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(e) => submitTriggerRef.current?.(e)}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}
